import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useParams, useLocation, Link } from 'react-router-dom';
import { useFeathers, isPermitted } from '../../app/util';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import {
  ChevronRightButton,
  LoadingSpinner,
  ProgressBar,
  FilterButton,
  Modal,
  DataDetailToolbar,
  DownloadData,
} from '../../components';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import moment from 'moment';
import qs from 'qs';
import { CreateMeasureModal } from '../../components/CreateMeasureModal';
import MoreVert from '@mui/icons-material/MoreVert';

const MEASURE_EXPORT_EXCLUDED_FIELDS = new Set([
  'districtType',
  'cities',
  'counties',
  'additionalDistricts',
  'excludedDistricts',
  'coveragePlan',
  'coverageDefault',
  'coveragePlanIsDefault',
  'coverageStatus',
  'attributedOrganizations',
]);

const getExportColumnName = (field) => {
  if (field === '_id') return 'Measure ID';
  if (field === '__v') return 'Version';

  return field
    .replace(/i18n$/i, ' I18n')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (character) => character.toUpperCase());
};

const getExportCellValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value.every((item) => ['string', 'number', 'boolean'].includes(typeof item))
      ? value.join(', ')
      : JSON.stringify(value);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
};

const transformMeasuresForExport = (measures) => (
  measures.map((measure) => {
    const district = measure.district;
    const districtIsPopulated = district && typeof district === 'object';
    const exportRow = {};

    Object.entries(measure).forEach(([field, value]) => {
      if (MEASURE_EXPORT_EXCLUDED_FIELDS.has(field)) return;

      if (field === 'district') {
        exportRow['District ID'] = districtIsPopulated ? district._id || '' : district || '';
        exportRow['District Long Name'] = districtIsPopulated ? district.longName || '' : '';
        return;
      }

      exportRow[getExportColumnName(field)] = getExportCellValue(value);
    });

    return exportRow;
  })
);

const Measures = ({

}) => {
  const rowsPerPageOptions = [15, 25, 50];
  const feathers = useFeathers();
  const history = useHistory();
  const location = useLocation();
  const election = useParams().key;
  const electionData = useSelector(state => state?.elections?.byKey[election])
  const user = useSelector(state => state.user);
  const addPermissions = isPermitted(user, ['editor', 'researcher', 'super-admin', 'admin', 'reviewer', 'publisher']);
  const resultsVisible = (electionData?.status || '').includes('results')

  /* data */
  const parsedQuery = useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location.search],
  );
  const {
    status: statusQuery,
    completion,
    coveragePlan,
    city,
    county,
    search = '',
    page,
    pageSize,
    sortBy,
    sortDirection,
  } = parsedQuery;
  const pageFromQuery = useMemo(() => {
    const parsedPage = parseInt(page, 10);
    if (Number.isNaN(parsedPage) || parsedPage < 1) return 0;
    return parsedPage - 1;
  }, [page]);
  const pageSizeFromQuery = useMemo(() => {
    const parsedSize = parseInt(pageSize, 10);
    return rowsPerPageOptions.includes(parsedSize) ? parsedSize : 15;
  }, [pageSize]);
  const combinedStatus = useMemo(() => {
    if (statusQuery === 'in-draft' || statusQuery === 'ready-to-review' || statusQuery === 'published') {
      return statusQuery;
    }
    if (statusQuery === 'published') return 'published';
    if (statusQuery === 'not-published' && completion === 'complete') return 'ready-to-review';
    if (statusQuery === 'not-published' || completion === 'incomplete') return 'in-draft';
    if (completion === 'complete') return 'ready-to-review';
    return null;
  }, [statusQuery, completion]);
  const [ countyOptions, setCountyOptions ] = useState([]);
  const [ cityOptions, setCityOptions ] = useState([]);

  const [ measureModalOpen, setMeasureModalOpen ] = useState(false);
  const [ data, setData ] = useState([]);
  const [ dataLoading, setDataLoading ] = useState(true);
  const [ activePage, setActivePage ] = useState(pageFromQuery);
  const [ totalNumber, setTotalNumber ] = useState(0);
  const [ searchFocusNonce, setSearchFocusNonce ] = useState(0);
  const [ selectedMeasureIds, setSelectedMeasureIds ] = useState([]);
  const [ isDeletingMeasures, setIsDeletingMeasures ] = useState(false);
  const [ rowsPerPage, setRowsPerPage ] = useState(pageSizeFromQuery);
  const [ moreMenuAnchorEl, setMoreMenuAnchorEl ] = useState(null);
  const [ exportMeasureModalOpen, setExportMeasureModalOpen ] = useState(false);
  const descendantOf = useMemo(() => electionData?.districts, [electionData]);

  useEffect(() => {
    if (rowsPerPage !== pageSizeFromQuery) {
      setRowsPerPage(pageSizeFromQuery);
      setActivePage(0);
    }
  }, [pageSizeFromQuery]);

  useEffect(() => {
    if (activePage !== pageFromQuery) {
      setActivePage(pageFromQuery);
    }
  }, [pageFromQuery]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'f') return;
      const target = e.target;
      const tag = target?.tagName?.toLowerCase();
      const editable =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable;
      if (editable) return;
      e.preventDefault();
      setSearchFocusNonce((n) => n + 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const updateQueryAndLoad = ({
    nextPage = activePage,
    nextStatus = combinedStatus,
    nextCoveragePlan = coveragePlan,
    nextCounty = county,
    nextCity = city,
    nextSearch = search,
    nextRowsPerPage = rowsPerPage,
    nextSortBy = sortBy,
    nextSortDirection = sortDirection,
  } = {}) => {
    setActivePage(nextPage);
    setRowsPerPage(nextRowsPerPage);
    const queryParams = {};
    if (nextPage > 0) queryParams.page = String(nextPage + 1);
    if (nextRowsPerPage !== 15) queryParams.pageSize = String(nextRowsPerPage);
    if (nextStatus) queryParams.status = nextStatus;
    if (nextCoveragePlan && nextCoveragePlan !== 'all') queryParams.coveragePlan = nextCoveragePlan;
    if (nextCounty) queryParams.county = nextCounty;
    if (nextCity) queryParams.city = nextCity;
    if (nextSearch) queryParams.search = nextSearch;
    if (nextSortBy && nextSortBy !== 'updatedAt') queryParams.sortBy = nextSortBy;
    if (nextSortDirection && nextSortDirection !== 'desc') queryParams.sortDirection = nextSortDirection;
    const queryString = qs.stringify(queryParams);
    history.replace(`${location.pathname}${queryString ? `?${queryString}` : ''}`);
    loadData(nextPage, nextStatus, nextCoveragePlan, nextSearch, nextCounty, nextCity, nextSortBy, nextSortDirection);
  };

  const loadData = useCallback(async (pageRef, statusRef, coveragePlanRef, searchTextRef, countyRef, cityRef, sortByRef, sortDirectionRef) => {
    setDataLoading(true)
    const activePageToUse = typeof(pageRef) === 'undefined' ? activePage : pageRef;
    const statusToRef = typeof statusRef === 'undefined' ? combinedStatus : statusRef;
    const sortByToRef = typeof sortByRef === 'undefined' ? sortBy : sortByRef;
    const sortDirectionToRef = typeof sortDirectionRef === 'undefined' ? sortDirection : sortDirectionRef;
    const sortOrder = sortDirectionToRef === 'asc' ? 1 : -1;
    const sortConfig = sortByToRef === 'title'
      ? { title: sortOrder, createdAt: -1 }
      : sortByToRef === 'districtSearchTerm'
        ? { districtSearchTerm: sortOrder, createdAt: -1 }
      : sortByToRef === 'updatedAt'
        ? { updatedAt: sortOrder, createdAt: -1 }
        : { ballotOrder: 1, title: 1, createdAt: -1 };
    const statusFilters = {};
    if (statusToRef === 'in-draft') {
      statusFilters.coverageStatus = 'not-published';
      statusFilters.status = 'incomplete';
    } else if (statusToRef === 'ready-to-review') {
      statusFilters.coverageStatus = 'not-published';
      statusFilters.status = 'complete';
    } else if (statusToRef === 'published') {
      statusFilters.coverageStatus = 'published';
    }

    const coveragePlanToRef = typeof coveragePlanRef === 'undefined' ? coveragePlan : coveragePlanRef;
    const coveragePlanModified = (!coveragePlanToRef || 'all' === coveragePlanToRef) ? undefined : coveragePlanToRef;

    const countyToRef = typeof countyRef === 'undefined' ? county : countyRef;
    const cityToRef = typeof cityRef === 'undefined' ? city : cityRef;
    const searchTextToRef = typeof searchTextRef === 'undefined' ? search : searchTextRef;
    const searchTextModified = searchTextToRef ? { 
      districtSearchTerm: { $search: searchTextToRef } 
        // { title: { $search: searchTextToRef } },
    } : {};
    const locationFilters = {};
    if (cityToRef) {
      locationFilters.cities = cityToRef;
    } else if (countyToRef) {
      locationFilters.counties = countyToRef;
    }
    try {
      const res = await feathers.getService('measures').find({ query: {
        election: election,
        $sort: sortConfig,
        ...statusFilters,
        ...(coveragePlanModified ? { coveragePlan: coveragePlanModified } : {}),
        ...locationFilters,
        ...searchTextModified,
        $limit: rowsPerPage,
        $skip: activePageToUse * rowsPerPage
      }})
      setData(res.data)
      setTotalNumber(res.total);
    } catch(err) {
      console.error(err)
    } finally {
      setDataLoading(false);
    }
  }, [ activePage, rowsPerPage, dataLoading, feathers, combinedStatus, search, coveragePlan, county, city, election, sortBy, sortDirection ])

  useEffect(() => {
    if(feathers) loadData()
  }, [ activePage, rowsPerPage, feathers, location.search ])

  useEffect(() => {
    const loadFilterOptions = async () => {
      if (!feathers) return;
      const districtsResponse = await feathers.getService('districts').find({
        query: {
          ...(descendantOf ? { $descendantOf: descendantOf, $nestChildren: false, $includeAncestor: true } : {}),
          $limit: 1000,
          $sort: { longName: 1 },
        },
      });
      const districts = districtsResponse?.data || [];
      setCountyOptions(
        districts
          .filter((district) => district.type === 'county')
          .map((district) => ({ label: district.longName, value: district._id }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
      setCityOptions(
        districts
          .filter((district) => district.type === 'city')
          .map((district) => ({ label: district.longName, value: district._id }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
    };

    loadFilterOptions();
  }, [feathers, descendantOf]);

  /* Table render config */
  const labelsForField = {
    title: 'Title',
    district: 'District',
    descriptionShort: 'Short description',
    coverageStatus: 'Status',
    result: 'Results linked',
    lastUpdated: 'Last updated'
  }
  const renderField = (label, value) => {
    if(label === 'result') {
      const numerator = typeof(value?.numOptionsLinked) === 'undefined' ? 0 : value?.numOptionsLinked;
      const denom = typeof(value?.numOptions) === 'undefined' ? 0 : value?.numOptions;
      const percentage = typeof(value?.percentageLinked) === 'undefined' ? 0 : value?.percentageLinked;
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='body1' style={{ opacity: 1.0, fontSize: '12px' }}>{numerator}/{denom} Options Linked</Typography>
          <ProgressBar percentage={percentage} />
        </div>
      )
    } else if(label === 'title') {
      return (
        <Typography
          variant='h4'
        >
          {value}
        </Typography>
      );
    } else if(label === 'coverageStatus') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
          <Typography
            variant='body1'
          >
            {value?.coverageStatus}
          </Typography>
          {
            value?.completionStatus &&
            <Typography
              variant='body2'
              style={{ opacity: 0.7 }}
            >
              {value?.completionStatus}
            </Typography>
          }
        </div>
      );
    } else {
      return (
        <Typography
          variant='body1'
        >
          {value}
        </Typography>
      );
    }
  }

  const dataMapped = data.map(measure => ({
    id: measure._id,
    to: `/elections/${election}/measures/${measure._id}`,
    title: measure.title,
    descriptionShort: measure.descriptionShort || 'No description.',
    district: measure.district?.longName,
    ...(resultsVisible
      ? { result: measure.result }
      : { 
        coverageStatus: {
          ...(measure.coveragePlan === 'no-coverage'
            ? { coverageStatus: `Won't cover` }
            : {
              coverageStatus: (
                measure.coverageStatus === 'published'
                  ? 'Published'
                  : 'Not published'
              ),
              completionStatus: measure.status === 'complete' ? 'Ready to review' : 'Not complete',
            }
          )
        }
      }
    ),
    lastUpdated: moment(measure.updatedAt).fromNow(),
  }))

  const tableColumns = useMemo(
    () => ['title', 'descriptionShort', 'district', resultsVisible ? 'result' : 'coverageStatus', 'lastUpdated'],
    [resultsVisible],
  );
  const sortableColumns = useMemo(
    () => ({
      title: 'title',
      district: 'districtSearchTerm',
      lastUpdated: 'updatedAt',
    }),
    [],
  );
  const currentPageMeasureIds = useMemo(() => data.map((measure) => measure._id), [data]);
  const hasSelections = selectedMeasureIds.length > 0;
  const allCurrentPageSelected = currentPageMeasureIds.length > 0
    && currentPageMeasureIds.every((id) => selectedMeasureIds.includes(id));
  const hasSomeCurrentPageSelected = currentPageMeasureIds.some((id) => selectedMeasureIds.includes(id));

  const onToggleSelectAllCurrentPage = (event) => {
    const isChecked = event.target.checked;
    setSelectedMeasureIds((previous) => {
      if (isChecked) {
        const merged = new Set([...previous, ...currentPageMeasureIds]);
        return Array.from(merged);
      }
      return previous.filter((id) => !currentPageMeasureIds.includes(id));
    });
  };

  const onToggleMeasureSelection = (measureId) => {
    setSelectedMeasureIds((previous) => (
      previous.includes(measureId)
        ? previous.filter((id) => id !== measureId)
        : [...previous, measureId]
    ));
  };

  const onDeleteSelectedMeasures = async () => {
    if (!selectedMeasureIds.length || !feathers || isDeletingMeasures) return;
    const confirmed = window.confirm(
      `Delete ${selectedMeasureIds.length} selected referendum${selectedMeasureIds.length === 1 ? '' : 's'}? This cannot be undone.`,
    );
    if (!confirmed) return;

    setIsDeletingMeasures(true);
    try {
      await Promise.all(
        selectedMeasureIds.map((measureId) => feathers.getService('measures').remove(measureId)),
      );
      const remainingTotal = Math.max(totalNumber - selectedMeasureIds.length, 0);
      const lastPageAfterDelete = Math.max(Math.ceil(remainingTotal / rowsPerPage) - 1, 0);
      const nextPage = Math.min(activePage, lastPageAfterDelete);
      setSelectedMeasureIds([]);
      updateQueryAndLoad({ nextPage });
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeletingMeasures(false);
    }
  };

  const onSortColumn = (columnKey) => {
    const sortField = sortableColumns[columnKey];
    if (!sortField) return;
    const isSameField = sortBy === sortField;
    const nextDirection = !isSameField
      ? 'asc'
      : sortDirection === 'asc'
        ? 'desc'
        : sortDirection === 'desc'
          ? null
          : 'asc';
    updateQueryAndLoad({
      nextPage: 0,
      nextSortBy: nextDirection ? sortField : null,
      nextSortDirection: nextDirection,
    });
  };

  return (
    <>
      <Wrapper>
        <DataDetailToolbar
          style={{ marginBottom: '24px' }}
          onBack={{ to: {
              pathname: `/elections/${election}`,
            }}}
          navTree={[
            {
              text: 'Elections',
              to: {
                pathname: `/elections`,
              }
            },
            {
              text: electionData?.name || 'Election',
              to: {
                pathname: `/elections/${election}`,
              }
            },
            { text: 'Ballot Referendums' }
          ]}
          actionButtonsComponent={
            addPermissions
              ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {hasSelections ? (
                    <>
                      <Button
                        variant='outlined'
                        onClick={() => setSelectedMeasureIds([])}
                        disabled={isDeletingMeasures}
                      >
                        Clear selection ({selectedMeasureIds.length})
                      </Button>
                      <Button
                        variant='contained'
                        color='error'
                        onClick={onDeleteSelectedMeasures}
                        disabled={isDeletingMeasures}
                      >
                        Delete measures
                      </Button>
                    </>
                  ) : (
                    <>
                      <IconButton
                        aria-label='More measure actions'
                        onClick={(event) => setMoreMenuAnchorEl(event.currentTarget)}
                        size='small'
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        anchorEl={moreMenuAnchorEl}
                        open={Boolean(moreMenuAnchorEl)}
                        onClose={() => setMoreMenuAnchorEl(null)}
                      >
                        <MenuItem
                          onClick={() => {
                            setMoreMenuAnchorEl(null);
                            history.push(`/elections/${election}/measure-lists`);
                          }}
                        >
                          Upload lists
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setExportMeasureModalOpen(true);
                            setMoreMenuAnchorEl(null);
                          }}
                        >
                          Export measure data
                        </MenuItem>
                      </Menu>
                      <Button variant='contained' color='primary' onClick={() => setMeasureModalOpen(true)}>New referendum</Button>
                    </>
                  )}
                </div>
              )
              : <div/>
          }
        />
        <DownloadData
          service='measures'
          serviceDisplay='Measures'
          defaultQuery={{ election }}
          open={exportMeasureModalOpen}
          onOpenChange={setExportMeasureModalOpen}
          hideButton
          transformData={transformMeasuresForExport}
        />
        <FilterBar>
          <FilterRow>
            <FilterButton
              label='County'
              value={county || null}
              onChange={(value) => {
                updateQueryAndLoad({
                  nextPage: 0,
                  nextCounty: value,
                  nextCity: null,
                });
              }}
              options={countyOptions}
              enableOptionSearch
            />
            <FilterButton
              label='City'
              value={city || null}
              onChange={(value) => {
                updateQueryAndLoad({
                  nextPage: 0,
                  nextCity: value,
                  nextCounty: null,
                });
              }}
              options={cityOptions}
              enableOptionSearch
            />
            <FilterButton
              label='Status'
              value={combinedStatus}
              onChange={(value) => {
                updateQueryAndLoad({
                  nextPage: 0,
                  nextStatus: value || null,
                });
              }}
              options={[
                { label: 'In draft', value: 'in-draft' },
                { label: 'Ready to review', value: 'ready-to-review' },
                { label: 'Published', value: 'published' },
              ]}
            />
            <FilterButton
              label='Coverage plan'
              value={coveragePlan && coveragePlan !== 'all' ? coveragePlan : null}
              onChange={(value) => {
                updateQueryAndLoad({
                  nextPage: 0,
                  nextCoveragePlan: value || 'all',
                });
              }}
              options={[
                { label: 'Auto coverage', value: 'auto-coverage' },
                { label: 'No coverage', value: 'no-coverage' },
              ]}
            />
          </FilterRow>
          <MeasureSearchSlot>
            <FilterButton
              label='Search'
              value={search || ''}
              onChange={(value) => {
                updateQueryAndLoad({
                  nextPage: 0,
                  nextSearch: value || '',
                });
              }}
              isSearch
              onSearch={(term) => {
                updateQueryAndLoad({
                  nextPage: 0,
                  nextSearch: term,
                });
              }}
              focusNonce={searchFocusNonce}
            />
          </MeasureSearchSlot>
        </FilterBar>
        {
          dataLoading &&
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LoadingSpinner />
          </div>
        }
        {
          !dataLoading && dataMapped?.length > 0 &&
          <>
            <MeasuresTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <HeaderCheckboxCell padding='checkbox'>
                      <Checkbox
                        checked={allCurrentPageSelected}
                        indeterminate={hasSomeCurrentPageSelected && !allCurrentPageSelected}
                        onChange={onToggleSelectAllCurrentPage}
                      />
                    </HeaderCheckboxCell>
                    {tableColumns.map((column) => (
                      <HeaderCell
                        key={column}
                        $sortable={Boolean(sortableColumns[column])}
                        onClick={() => onSortColumn(column)}
                      >
                        <Typography variant='h5'>
                          {labelsForField[column] || ''}
                          {sortableColumns[column] && sortBy === sortableColumns[column] && (
                            <SortIndicator>{sortDirection === 'asc' ? ' \u2191' : ' \u2193'}</SortIndicator>
                          )}
                        </Typography>
                      </HeaderCell>
                    ))}
                    <HeaderActionCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataMapped.map(({ id, to, ...measureFields }) => (
                    <MeasureRow key={to} component={Link} to={to}>
                      <BodyCheckboxCell
                        padding='checkbox'
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        <Checkbox
                          checked={selectedMeasureIds.includes(id)}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          onChange={(event) => {
                            event.stopPropagation();
                            onToggleMeasureSelection(id);
                          }}
                        />
                      </BodyCheckboxCell>
                      {tableColumns.map((column) => (
                        <BodyCell key={column}>
                          {renderField(column, measureFields[column])}
                        </BodyCell>
                      ))}
                      <ActionCell>
                        <ChevronRightButton />
                      </ActionCell>
                    </MeasureRow>
                  ))}
                </TableBody>
              </Table>
            </MeasuresTableContainer>
            <TablePagination
              component='div'
              count={totalNumber}
              page={activePage}
              onPageChange={(_, nextPage) => updateQueryAndLoad({ nextPage })}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                updateQueryAndLoad({
                  nextPage: 0,
                  nextRowsPerPage: parseInt(event.target.value, 10),
                });
              }}
              rowsPerPageOptions={rowsPerPageOptions}
            />
          </>
        }
        {
          !dataLoading && dataMapped?.length === 0 &&
          <div style={{ width: 'calc(100% - 36px)', padding: '36px', dislay: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant='h3' style={{ opacity: 0.7, letterSpacing: '1px', textAlign: 'center' }}>
              NO BALLOT REFERENDA
            </Typography>
          </div>
        }
      </Wrapper>
      {
        measureModalOpen &&
        <Modal onClose={() => setMeasureModalOpen(false)}>
          <CreateMeasureModal
            election={electionData}
            redirectOnFinish={true}
            onClose={() => setMeasureModalOpen(false)}
          />
        </Modal>
      }
    </>
  )
}

const Wrapper = styled.div`
  width: calc(100% - 24px - 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const MeasuresTableContainer = styled(TableContainer)`
  border-radius: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const HeaderCell = styled(TableCell)`
  background-color: #f8f8f8;
  border-bottom: 1px solid #e3e3e3;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
`;

const HeaderCheckboxCell = styled(HeaderCell)`
  width: 52px;
  min-width: 52px;
`;

const BodyCell = styled(TableCell)`
  border-bottom: 1px solid #efefef;
`;

const BodyCheckboxCell = styled(BodyCell)`
  width: 52px;
  min-width: 52px;
`;

const ActionCell = styled(TableCell)`
  width: 48px;
  min-width: 48px;
  border-bottom: 1px solid #efefef;
  text-align: right;
`;

const HeaderActionCell = styled(ActionCell)`
  background-color: #f8f8f8;
  border-bottom: 1px solid #e3e3e3;
`;

const SortIndicator = styled.span`
  font-size: 16px;
  font-weight: 700;
  opacity: 0.95;
  line-height: 1;
  margin-left: 4px;
`;

const MeasureRow = styled(TableRow)`
  cursor: pointer;
  text-decoration: none;
  color: inherit;

  &:hover {
    background-color: #f8f8f8;
  }
`;

export default Measures;

const FilterBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: stretch;
  gap: 8px 12px;
  padding: 0 0 24px;
  width: 100%;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
`;

const MeasureSearchSlot = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-left: auto;
  min-width: 240px;
  max-width: 240px;
  width: min(240px, 100%);
`;
